import { gunzipSync } from 'fflate';

/**
 * Port of the decoding half of `eeg_raw_data_cubit.dart`.
 *
 * This module is pure and environment-agnostic — no DOM, no Appwrite — so it
 * runs unchanged on the main thread, inside the Web Worker, and under the
 * unit tests that pin the byte order.
 */

/** The only file extension this feature knows how to handle. */
export const RAW_FILE_SUFFIX = '.raw.bin.gz';

// ---------------------------------------------------------------------------
// Sample format of the uploaded `.raw.bin`.
//
// The writer concatenates the BLE chunk payloads verbatim, so this MUST match
// what the headband firmware emits: two bytes per sample, two's-complement,
// big-endian — the usual EEG / TGAM-style convention. If the firmware ever
// changes, these three constants are the only edit.
// ---------------------------------------------------------------------------
export const BYTES_PER_SAMPLE = 2;
export const SAMPLE_ENDIAN_BIG = true;
export const SAMPLES_ARE_SIGNED = true;

/** How many samples the text preview prints before eliding the middle. */
const PREVIEW_HEAD = 100;
const PREVIEW_TAIL = 10;

/** Raised when the stored file is not a usable `*.raw.bin.gz` payload. */
export class EegRawDataFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EegRawDataFormatError';
  }
}

export interface SampleStats {
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  /** Root mean square — the conventional amplitude measure for a raw trace. */
  readonly rms: number;
}

/**
 * Decodes the concatenated chunk payload into samples.
 *
 * A trailing partial sample is dropped rather than treated as fatal: it means
 * one chunk was cut short, and the rest of the session is still good.
 */
export function decodeSamples(bytes: Uint8Array): Int16Array {
  if (bytes.length === 0) {
    throw new EegRawDataFormatError('The raw data file is empty.');
  }

  const count = Math.floor(bytes.length / BYTES_PER_SAMPLE);
  if (count === 0) {
    throw new EegRawDataFormatError(
      `The raw data file holds only ${bytes.length} byte(s) — ` +
        `too short for a single ${BYTES_PER_SAMPLE}-byte sample.`,
    );
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Int16Array(count);
  const littleEndian = !SAMPLE_ENDIAN_BIG;

  for (let i = 0; i < count; i += 1) {
    const offset = i * BYTES_PER_SAMPLE;
    samples[i] = SAMPLES_ARE_SIGNED
      ? view.getInt16(offset, littleEndian)
      : view.getUint16(offset, littleEndian);
  }

  return samples;
}

export function computeStats(samples: Int16Array): SampleStats {
  if (samples.length === 0) {
    return { min: 0, max: 0, mean: 0, rms: 0 };
  }

  let min = samples[0]!;
  let max = samples[0]!;
  let sum = 0;
  let sumSquares = 0;

  for (let i = 0; i < samples.length; i += 1) {
    const value = samples[i]!;
    if (value < min) min = value;
    if (value > max) max = value;
    sum += value;
    sumSquares += value * value;
  }

  return {
    min,
    max,
    mean: sum / samples.length,
    rms: Math.sqrt(sumSquares / samples.length),
  };
}

/**
 * The on-screen preview text, byte-for-byte equivalent to the Flutter app's
 * `_preview` — same header line, same elision, same warning wording.
 */
export function buildPreview(samples: Int16Array, byteLength: number, stats: SampleStats): string {
  const lines: string[] = [];

  lines.push(
    `Decoded ${samples.length} samples ` +
      `(${BYTES_PER_SAMPLE * 8}-bit ${SAMPLES_ARE_SIGNED ? 'signed' : 'unsigned'}, ` +
      `${SAMPLE_ENDIAN_BIG ? 'big' : 'little'} endian)`,
  );

  const remainder = byteLength % BYTES_PER_SAMPLE;
  if (remainder !== 0) {
    lines.push(
      `Warning: ${remainder} trailing byte(s) ignored — the last chunk looks truncated.`,
    );
  }

  if (samples.length > 0) {
    lines.push(`min ${stats.min} · max ${stats.max} · mean ${stats.mean.toFixed(2)}`);
  }
  lines.push('');

  if (samples.length > PREVIEW_HEAD + PREVIEW_TAIL) {
    lines.push(joinRange(samples, 0, PREVIEW_HEAD));
    lines.push(
      `… ${samples.length - PREVIEW_HEAD - PREVIEW_TAIL} more ` +
        '(use Copy for all samples, Download for the binary) …',
    );
    lines.push(joinRange(samples, samples.length - PREVIEW_TAIL, samples.length));
  } else {
    lines.push(joinRange(samples, 0, samples.length));
  }

  return `${lines.join('\n')}\n`;
}

function joinRange(samples: Int16Array, start: number, end: number): string {
  const parts: string[] = [];
  for (let i = start; i < end; i += 1) parts.push(String(samples[i]));
  return parts.join(', ');
}

/** Every decoded sample, comma separated — what Copy and the .txt export use. */
export function samplesToText(samples: Int16Array): string {
  return joinRange(samples, 0, samples.length);
}

/**
 * Gunzips the payload. A corrupt archive is an error, never a silent
 * pass-through of the still-compressed bytes.
 */
export function decompress(bytes: Uint8Array, fileName: string): Uint8Array {
  try {
    const out = gunzipSync(bytes);
    if (out.length === 0) {
      throw new EegRawDataFormatError('The raw data file decompressed to nothing.');
    }
    return out;
  } catch (error) {
    if (error instanceof EegRawDataFormatError) throw error;
    console.warn(`GZIP decompression failed for ${fileName}:`, error);
    throw new EegRawDataFormatError(
      'The raw data file could not be decompressed — it may be corrupt.',
    );
  }
}

/** `session.raw.bin.gz` → `session.raw.bin`. */
export function effectiveName(fileName: string): string {
  return fileName.toLowerCase().endsWith('.gz') ? fileName.slice(0, -3) : fileName;
}

export function hasRawSuffix(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(RAW_FILE_SUFFIX);
}

/**
 * Min/max decimation for the waveform canvas.
 *
 * Drawing 400,000 points into 1,000 pixels is both slow and a lie — it throws
 * away the peaks that matter in an EEG trace. Instead each output column keeps
 * the true minimum and maximum of the samples that fall in it, so the envelope
 * of the signal survives at any zoom level.
 */
export function decimateMinMax(
  samples: Int16Array,
  start: number,
  end: number,
  columns: number,
): { min: Float32Array; max: Float32Array; columns: number } {
  const span = Math.max(0, Math.min(end, samples.length) - Math.max(0, start));
  const outColumns = Math.max(1, Math.min(columns, span || 1));
  const min = new Float32Array(outColumns);
  const max = new Float32Array(outColumns);

  if (span === 0) return { min, max, columns: outColumns };

  const from = Math.max(0, start);
  const perColumn = span / outColumns;

  for (let column = 0; column < outColumns; column += 1) {
    const sliceStart = from + Math.floor(column * perColumn);
    const sliceEnd = column === outColumns - 1 ? from + span : from + Math.floor((column + 1) * perColumn);

    let columnMin = samples[sliceStart] ?? 0;
    let columnMax = columnMin;

    for (let i = sliceStart + 1; i < sliceEnd; i += 1) {
      const value = samples[i]!;
      if (value < columnMin) columnMin = value;
      if (value > columnMax) columnMax = value;
    }

    min[column] = columnMin;
    max[column] = columnMax;
  }

  return { min, max, columns: outColumns };
}

/**
 * A fixed-bin histogram of sample amplitudes, used by the analytics module.
 */
export function histogram(samples: Int16Array, bins: number, min: number, max: number): Uint32Array {
  const counts = new Uint32Array(bins);
  if (samples.length === 0 || max <= min) return counts;

  const scale = bins / (max - min);
  for (let i = 0; i < samples.length; i += 1) {
    const index = Math.min(bins - 1, Math.max(0, Math.floor((samples[i]! - min) * scale)));
    counts[index] = (counts[index] ?? 0) + 1;
  }
  return counts;
}
